import { Button } from "@/components/ui/button";
import { SearchableDropdown } from "@/components/ui/searchable-dropdown";
import { Textarea } from "@/components/ui/textarea";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { Switch } from "@/components/switch";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Info, MessageCircle } from "lucide-react";
import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import {
  usePrompt,
  usePromptRegistryObjects,
  usePromptVersions,
} from "@/queries";
import {
  ConversationSimulatorConfiguration,
  ReportColumn,
} from "@/types/evaluate";
import { formatInputVariable } from "@/utils/evaluate";
import { PencilIcon } from "@heroicons/react/outline";
import { useCallback, useMemo, useState } from "react";
import { ModalStep } from "../ModalRouter";
import SourceName from "../SourceName";
import { ConversationSamplesDialog } from "./ConversationSamplesDialog";

const HomeStep = ({
  navigateAway,
  navigateNext,
  setCurrentNavigation,
  availableColumns,
  configuration,
  setConfiguration,
  columnName,
  setColumnName,
  editable,
  columnId,
  isLoading = false,
}: {
  navigateAway: () => void;
  navigateNext: () => void;
  setCurrentNavigation: (navigation: string) => void;
  availableColumns: ReportColumn[];
  configuration: Partial<ConversationSimulatorConfiguration>;
  setConfiguration: (
    data:
      | Partial<ConversationSimulatorConfiguration>
      | ((
          prevState: Partial<ConversationSimulatorConfiguration>,
        ) => Partial<ConversationSimulatorConfiguration>),
  ) => void;
  columnName: string;
  setColumnName: (name: string) => void;
  editable: boolean;
  columnId?: number;
  isLoading?: boolean;
}) => {
  const authContext = useAuth();
  const userContext = useUser();
  const workspaceId = userContext?.activeWorkspaceId!;
  const userToken = authContext?.userToken!;
  const [nameIsValid, setNameIsValid] = useState(true);
  const [personaColumnEnabled, setPersonaColumnEnabled] = useState<boolean>(
    !!configuration.user_persona_source,
  );
  const [showSamplesDialog, setShowSamplesDialog] = useState<boolean>(false);

  const promptRegistryObjects = usePromptRegistryObjects(userToken, {
    workspaceId,
    perPage: Number.MAX_SAFE_INTEGER,
  });

  const templates =
    promptRegistryObjects.data?.pages.flatMap((page) => page.items) || [];

  const selectedPromptRegistry = templates.find(
    (template) => template.prompt_name === configuration.template?.name,
  );

  const promptVersionQuery = usePromptVersions(userToken, {
    workspaceId,
    promptRegistryId: selectedPromptRegistry?.id,
    perPage: Number.MAX_SAFE_INTEGER,
  });
  const promptVersions =
    promptVersionQuery.data?.pages.flatMap((page) => page.prompt_versions) ||
    [];

  const version =
    promptVersions.find(
      (version) => version?.number === configuration.template?.version_number,
    ) || promptVersions.at(0);

  const promptTemplateVersionQuery = usePrompt(
    userToken,
    {
      workspace_id: workspaceId,
      prompt_name: selectedPromptRegistry?.prompt_name || "",
      number: !configuration?.template?.label ? version?.number : undefined,
      label: configuration?.template?.label,
    },
    {
      enabled: !!(
        selectedPromptRegistry?.prompt_name &&
        (version?.number || configuration?.template?.label)
      ),
    },
  );

  const promptTemplateVersion = promptTemplateVersionQuery.data;

  const inputVariables =
    promptTemplateVersion?.rendered_template?.input_variables || [];

  const onUpdateInputVariableMapping = useCallback(
    (inputVariable: string, columnName: string) => {
      const isAlreadyMapped =
        configuration.prompt_template_variable_mappings?.[inputVariable] ===
        columnName;
      const newInputVariableMapping = {
        ...configuration.prompt_template_variable_mappings,
        [inputVariable]: isAlreadyMapped ? "" : columnName,
      };

      setConfiguration({
        ...configuration,
        prompt_template_variable_mappings: newInputVariableMapping,
      });
    },
    [configuration, setConfiguration],
  );

  const conversationSamples = useMemo(() => {
    return configuration.conversation_samples || [];
  }, [configuration.conversation_samples]);

  // Filter available columns to only include columns positioned to the left (earlier in pipeline)
  const filteredAvailableColumns = useMemo(() => {
    const currentPosition = availableColumns.find((col) => col.id === columnId)
      ?.position;
    if (currentPosition === undefined) {
      // If we don't have a position (new column), show all columns
      return availableColumns;
    }
    // Only show columns with position less than current column
    return availableColumns.filter((col) => col.position < currentPosition);
  }, [availableColumns, columnId]);

  const validateAndSave = () => {
    const isNameValid = columnName && columnName.trim() !== "";
    setNameIsValid(isNameValid);

    if (isNameValid && configuration.template?.name) {
      navigateNext();
    }
  };

  const isButtonDisabled = useMemo(() => {
    return (
      !configuration.template?.name || !columnName || columnName.trim() === ""
    );
  }, [configuration.template?.name, columnName]);

  return (
    <ModalStep
      navigatePrevious={navigateAway}
      navigateNext={isButtonDisabled ? undefined : validateAndSave}
      nextButtonText="Save Step"
      previousButtonText="Previous"
      isLoading={isLoading}
    >
      <div className="grid grid-cols-2 items-center gap-4">
        <h2 className="col-span-2 text-lg font-semibold">
          Configure Conversation Simulator
        </h2>

        <SourceName
          name={columnName}
          setName={setColumnName}
          isNode={false}
          readonly={!editable}
          isValid={nameIsValid}
          columnCount={availableColumns.length}
          variant="PROMPT_TEMPLATE"
        />

        <div>Prompt template:</div>
        {configuration.template?.name ? (
          <div className="flex items-center space-x-2">
            <span className="flex-1 break-all text-gray-600">
              {configuration.template.name}
            </span>
            <Button
              size="tinyIcon"
              variant="outline"
              onClick={() => setCurrentNavigation("select-prompt-template")}
            >
              <PencilIcon className="inline-block h-4 w-4 text-gray-600" />
            </Button>
          </div>
        ) : (
          <div>
            <div className="flex items-center space-x-2">
              <Button
                onClick={() => setCurrentNavigation("select-prompt-template")}
                className="w-full bg-blue-500 px-4 py-2 text-center text-white"
              >
                Select template
              </Button>
            </div>
          </div>
        )}

        <div>Model engine:</div>
        {configuration.template?.name && configuration.engine ? (
          <div className="flex items-center space-x-2">
            <span className="flex-1 break-all text-gray-600">
              {configuration.engine.model}
            </span>
            <Button
              size="tinyIcon"
              variant="outline"
              onClick={() => setCurrentNavigation("select-engine-and-params")}
            >
              <PencilIcon className="inline-block h-4 w-4 text-gray-600" />
            </Button>
          </div>
        ) : configuration.template?.name ? (
          <span className="text-gray-400">Default</span>
        ) : (
          <Button
            onClick={() => setCurrentNavigation("select-engine-and-params")}
            disabled={!configuration.template?.name}
          >
            Configure engine
          </Button>
        )}

        {configuration.template?.name && (
          <>
            <div className="col-span-2">Input variables:</div>
            {inputVariables.length === 0 ? (
              <div className="col-span-2 rounded-md border border-gray-200 bg-gray-50 p-3">
                <div className="text-sm text-gray-500">
                  {`This template does not have any input variables. Please add {variables} using f-string or jinja.`}
                </div>
              </div>
            ) : (
              <div className="col-span-2 mx-3 rounded-md border border-gray-200 bg-gray-50">
                <div className="flex flex-row justify-between">
                  <p className="ml-2 mt-2 text-xs text-gray-500">
                    {
                      Object.entries(
                        configuration.prompt_template_variable_mappings || {},
                      ).length
                    }
                    /{inputVariables.length}
                  </p>

                  <Button
                    onClick={() => {
                      const newMappings = {
                        ...(configuration.prompt_template_variable_mappings ||
                          {}),
                      };
                      inputVariables.forEach((variable) => {
                        const matchingColumn = filteredAvailableColumns.find(
                          (column) =>
                            column.name === variable ||
                            column.name === `variable.${variable}` ||
                            column.name === `metadata.${variable}`,
                        );
                        if (matchingColumn) {
                          newMappings[variable] = matchingColumn.name;
                        }
                      });
                      setConfiguration({
                        ...configuration,
                        prompt_template_variable_mappings: newMappings,
                      });
                    }}
                    variant="link"
                    size="sm"
                    className="inline-flex items-center"
                  >
                    Click to auto-fill ✨
                  </Button>
                </div>
                <div className="max-h-56 overflow-y-auto px-3 py-2">
                  {filteredAvailableColumns.length === 0 ? (
                    <div className="flex flex-col items-center justify-center space-y-2 p-4 pt-0 text-center">
                      <div className="text-sm font-medium text-gray-900">
                        No prior columns available
                      </div>
                      <div className="text-xs text-gray-500">
                        Add a prior column to map variables from.
                      </div>
                    </div>
                  ) : (
                    (inputVariables || []).map((variable: string) => (
                      <div
                        className="grid grid-cols-3 items-center space-x-2 space-y-2"
                        key={`var-${variable}`}
                      >
                        <div className="col-span-1 mr-2 mt-2 break-all text-right font-mono text-sm text-gray-600">
                          {`{${variable}}`}
                        </div>
                        <div className="col-span-2 w-full">
                          <SearchableDropdown
                            categories={[
                              {
                                label: "Available Sources",
                                items: filteredAvailableColumns.map(
                                  (column) => ({
                                    id: column.name,
                                    label: formatInputVariable(
                                      column.column_type,
                                      column.name,
                                    ),
                                    onClick: () =>
                                      onUpdateInputVariableMapping(
                                        variable,
                                        column.name,
                                      ),
                                  }),
                                ),
                              },
                            ]}
                            selectedItem={
                              configuration.prompt_template_variable_mappings?.[
                                variable
                              ]
                                ? {
                                    label: formatInputVariable(
                                      filteredAvailableColumns.find(
                                        (col) =>
                                          col.name ===
                                          configuration
                                            .prompt_template_variable_mappings?.[
                                            variable
                                          ],
                                      )?.column_type || "unknown",
                                      configuration
                                        .prompt_template_variable_mappings?.[
                                        variable
                                      ] || "",
                                    ),
                                  }
                                : undefined
                            }
                            onSelect={(item) =>
                              onUpdateInputVariableMapping(variable, item.id)
                            }
                            readonly={!editable}
                          />
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </>
        )}

        <div>User Persona:</div>
        {personaColumnEnabled ? (
          <DropdownMenu>
            <DropdownMenuTrigger
              disabled={!editable}
              className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 data-[placeholder]:text-muted-foreground"
            >
              <div className="flex w-full items-center justify-between">
                {configuration.user_persona_source ? (
                  <span className="text-left">
                    {formatInputVariable(
                      filteredAvailableColumns.find(
                        (col) => col.name === configuration.user_persona_source,
                      )?.column_type || "unknown",
                      configuration.user_persona_source,
                    )}
                  </span>
                ) : (
                  <span className="font-normal text-gray-500">
                    Select a column...
                  </span>
                )}
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-full">
              {filteredAvailableColumns.length > 0 ? (
                filteredAvailableColumns.map((column) => (
                  <DropdownMenuItem
                    key={column.name}
                    onSelect={() =>
                      setConfiguration({
                        ...configuration,
                        user_persona_source: column.name,
                      })
                    }
                  >
                    {formatInputVariable(column.column_type, column.name)}
                  </DropdownMenuItem>
                ))
              ) : (
                <DropdownMenuItem disabled>
                  No columns available (need columns positioned earlier)
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <Textarea
            placeholder="Describe the persona of the user in the conversation..."
            value={configuration.user_persona || ""}
            onChange={(e) =>
              setConfiguration({
                ...configuration,
                user_persona: e.target.value,
              })
            }
            disabled={!editable}
            rows={3}
          />
        )}

        <div className="col-span-2 mt-2 flex items-center gap-2">
          <input
            type="checkbox"
            id="use-persona-column"
            checked={personaColumnEnabled}
            onChange={(e) => {
              setPersonaColumnEnabled(e.target.checked);
              if (e.target.checked) {
                setConfiguration({
                  ...configuration,
                  user_persona: undefined,
                });
              } else {
                setConfiguration({
                  ...configuration,
                  user_persona_source: undefined,
                });
              }
            }}
            disabled={!editable}
            className="cursor-pointer"
          />
          <label
            htmlFor="use-persona-column"
            className="cursor-pointer text-sm"
          >
            Use column variable for user persona
          </label>
          <BasicTooltip content="You can use a column variable for the user persona. This will use the value of the column as the persona description.">
            <Info className="h-4 w-4 cursor-pointer hover:text-gray-500" />
          </BasicTooltip>
        </div>

        <div className="col-span-2 mt-6">
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="advanced-controls" className="border-none">
              <AccordionTrigger className="py-2 text-base font-medium text-gray-900 hover:no-underline">
                Advanced Controls
              </AccordionTrigger>
              <AccordionContent className="pt-2">
                <div className="grid grid-cols-2 items-center gap-4">
                  <div>User Goes First:</div>
                  <div className="flex items-center justify-end gap-2">
                    <Switch
                      checked={configuration.is_user_first ?? false}
                      onChange={(checked) =>
                        setConfiguration({
                          ...configuration,
                          is_user_first: checked,
                        })
                      }
                      disabled={!editable}
                      size="sm"
                    />
                    <BasicTooltip content="Whether the user starts the conversation or the assistant does">
                      <Info className="h-4 w-4 cursor-pointer hover:text-gray-500" />
                    </BasicTooltip>
                  </div>

                  <div>Conversation Samples:</div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowSamplesDialog(true)}
                    disabled={!editable}
                    className="w-full justify-between"
                  >
                    <div className="flex items-center gap-2">
                      <MessageCircle className="h-4 w-4" />
                      <span>
                        {conversationSamples.length === 0
                          ? "No samples"
                          : `${conversationSamples.length} sample${
                              conversationSamples.length !== 1 ? "s" : ""
                            }`}
                      </span>
                    </div>
                    <span className="text-xs text-gray-500">Manage</span>
                  </Button>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>

      <ConversationSamplesDialog
        open={showSamplesDialog}
        onOpenChange={setShowSamplesDialog}
        samples={conversationSamples}
        onSamplesChange={(samples) =>
          setConfiguration({
            ...configuration,
            conversation_samples: samples,
          })
        }
        disabled={!editable}
      />
    </ModalStep>
  );
};

export default HomeStep;
